import { useCallback, useEffect, useRef, useState } from "react";

import { type Map as MapType } from "leaflet";

import { UserRole, type MapBuilding } from "@eisox/api/core-api";
import { useAuthContext } from "@eisox/auth";
import { CanAccess, IconButton, Page, Sidebar } from "@eisox/design-system";
import { Map } from "@eisox/design-system/map";
import { PanelLeftIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { useSuspenseQueries } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";

import { authApi, buildingApi } from "~/api";
import { SuspendedAccountDialog } from "~/components/suspended-account-dialog";
import { BuildingMapMarker, Combobox, CreateBuildingDrawer, type BuildingsComboboxHandle } from "~/features/buildings";
import {
  buildingsMapSearchSchema,
  isCompleteMapView,
  isSameMapSearch,
  setLastBuildingsMapSearch,
  toMapSearch,
} from "~/features/buildings/helpers/buildings-map-search.helper";
import { useCommandPaletteShortcut } from "~/features/command-palette";
import { getPermissions } from "~/helpers/auth/auth.helper";
import { documentTitleHead } from "~/helpers/document-title.helper";
import { m } from "~/paraglide/messages";

export const Route = createFileRoute("/_authenticated/_no-building/buildings")({
  validateSearch: buildingsMapSearchSchema,
  loader: async ({ context: { queryClient } }) => {
    return queryClient.ensureQueryData(buildingApi.queries.all);
  },
  head: () => documentTitleHead({ page: m.my_buildings() }),
  component: RouteComponent,
});

function RouteComponent() {
  const mapRef = useRef<MapType>(null);
  const comboboxRef = useRef<BuildingsComboboxHandle>(null);

  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  const mapView = isCompleteMapView(search) ? search : undefined;

  const isMobile = useMediaQuery("(max-width: 768px)");

  const { user } = useAuthContext(Route.id);

  const [{ data: buildings }, { data: roles }] = useSuspenseQueries({
    queries: [buildingApi.queries.all, authApi.queries.roles],
  });

  const [suspendedBuildingId, setSuspendedBuildingId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (mapView) {
      setLastBuildingsMapSearch(mapView);
    }
  }, [mapView]);

  const handleViewChange = useCallback(
    (view: Map.MapView) => {
      const next = toMapSearch(view);
      setLastBuildingsMapSearch(next);

      const hasLeftPage = router.state.location.pathname !== Route.fullPath;
      if (hasLeftPage || isSameMapSearch(search, next)) return;

      void navigate({ search: next, replace: true });
    },
    [navigate, router, search],
  );

  useCommandPaletteShortcut({
    onTrigger: () => {
      comboboxRef.current?.focus();
    },
  });

  const handleSearch = useCallback((matches: MapBuilding[]) => {
    if (matches.length === 0) return;
    mapRef.current?.flyToBounds(
      matches.map(b => [b.latitude, b.longitude]),
      { duration: 0.8, easeLinearity: 0.3 },
    );
  }, []);

  const handleSuspendedBuildingClick = () => {
    if (suspendedBuildingId) {
      setSuspendedBuildingId(undefined);
    }
  };

  const isCreator = user?.isCreator;
  const isAdmin = !!suspendedBuildingId && getPermissions(roles, suspendedBuildingId).role === UserRole.OWNER;

  return (
    <Page.Root>
      <Page.Header>
        <div className={"gap-2.5 flex h-auto items-center"}>
          {isMobile && (
            <Sidebar.Trigger asChild>
              <IconButton>
                <PanelLeftIcon />
              </IconButton>
            </Sidebar.Trigger>
          )}
          <Page.Title>{m.my_buildings()}</Page.Title>
        </div>
        <Combobox ref={comboboxRef} buildings={buildings} onSearch={handleSearch} />
        <CanAccess isAuthorized={isCreator && !isMobile}>
          <CreateBuildingDrawer className="ml-auto" />
        </CanAccess>
      </Page.Header>
      <Page.Content data-testid="Houses">
        <Map.Root
          ref={mapRef}
          className="h-full w-full"
          center={mapView ? [mapView.lat, mapView.lng] : undefined}
          zoom={mapView?.zoom}
          fitMarkers={mapView === undefined}
        >
          <Map.ViewTracker onViewChange={handleViewChange} />
          <Map.MarkerClusterGroup>
            {buildings.map(building => {
              const { role } = getPermissions(roles, building.id);
              return (
                <BuildingMapMarker
                  key={building.id}
                  building={building}
                  role={role}
                  onSuspendedClick={setSuspendedBuildingId}
                />
              );
            })}
          </Map.MarkerClusterGroup>
        </Map.Root>
      </Page.Content>
      <SuspendedAccountDialog
        open={!!suspendedBuildingId}
        onOpenChange={handleSuspendedBuildingClick}
        isAdmin={isAdmin}
      />
    </Page.Root>
  );
}
